<template>
  <div class="flex w-full justify-start">
    <div class="flex max-w-[92%] gap-3 sm:max-w-[80%]">
      <div
        class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
        <Icon icon="heroicons:question-mark-circle" class="size-5" />
      </div>
      <div
        class="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-iris-200 bg-iris-50/80 px-5 py-4 shadow-sm dark:border-iris-800 dark:bg-iris-950/30">
        <!-- The question itself is the assistant message right above this card. -->

        <!-- Yes / No -->
        <div v-if="question.input_type === 'yes_no'" class="flex flex-wrap gap-2">
          <button
            class="cursor-pointer rounded-xl bg-iris-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-iris-500 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="disabled" @click="submit('Yes')">
            Yes
          </button>
          <button
            class="cursor-pointer rounded-xl border border-charcoal-200 bg-white px-5 py-2.5 text-sm font-medium text-charcoal-700 transition-colors hover:bg-charcoal-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-charcoal-700 dark:bg-charcoal-850 dark:text-charcoal-200 dark:hover:bg-charcoal-800"
            :disabled="disabled" @click="submit('No')">
            No
          </button>
        </div>

        <!-- Single choice -->
        <div v-else-if="question.input_type === 'single_choice'" class="flex flex-col gap-2">
          <button v-for="option in options" :key="option"
            class="w-full cursor-pointer rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            :class="'border-charcoal-200 bg-white text-charcoal-700 hover:border-iris-400 hover:bg-iris-50 dark:border-charcoal-700 dark:bg-charcoal-850 dark:text-charcoal-200 dark:hover:border-iris-500 dark:hover:bg-iris-950/40'"
            :disabled="disabled" @click="submit(option)">
            {{ option }}
          </button>
        </div>

        <!-- Multi choice -->
        <div v-else-if="question.input_type === 'multi_choice'" class="flex flex-col gap-3">
          <label v-for="option in options" :key="option"
            class="flex cursor-pointer items-center gap-2.5 text-sm text-charcoal-800 dark:text-charcoal-200">
            <input type="checkbox" :value="option" v-model="selected" :disabled="disabled"
              class="size-4 rounded border-charcoal-300 text-iris-600 focus:ring-iris-400 dark:border-charcoal-600" />
            <span>{{ option }}</span>
          </label>
          <div>
            <button
              class="cursor-pointer rounded-xl bg-iris-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-iris-500 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="disabled || !selected.length" @click="submitMulti">
              Submit
            </button>
          </div>
        </div>

        <!-- text / unknown input types: answer via the regular chat input below -->
        <p v-else class="text-xs italic text-charcoal-500 dark:text-charcoal-400">
          Type your answer in the chat below.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'

const props = defineProps({
  question: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['answer'])

const selected = ref([])

const options = computed(() => (Array.isArray(props.question?.options) ? props.question.options : []))

watch(() => props.question, () => {
  selected.value = []
})

// A bare "Yes" tells the model nothing on its own, so the answer we send restates
// the question while the chat still shows just what the user picked.
function buildAnswer(value) {
  const prompt = typeof props.question?.prompt === 'string' ? props.question.prompt.trim() : ''

  return {
    display: value,
    text: prompt ? `${value} — in reply to: ${prompt}` : value,
  }
}

function submit(value) {
  if (props.disabled) return
  emit('answer', buildAnswer(value))
}

function submitMulti() {
  if (!selected.value.length || props.disabled) return
  emit('answer', buildAnswer(selected.value.join(', ')))
}
</script>
